import { getErrorFromFieldState } from '@utils/formHelpers';
import { memo } from 'react';
import { Control, FieldPath, FieldValues, useController, useFormContext } from 'react-hook-form';
import { BlockSelectField } from './BlockSelectField';
import { PureSelectProps } from './BlockSelectField/BlockSelectField';

type RHFBlockSelectFieldProps<TFieldValues extends FieldValues = FieldValues> = Omit<
  PureSelectProps,
  'value' | 'onChange' | 'error'
> & {
  name: FieldPath<TFieldValues>;
  control?: Control<TFieldValues>;
  parse?: (option: any) => any;
  format?: (value: any, options: any[]) => any;
  handleAfterChange?: (selected: any) => void;
};

const RHFBlockSelectFieldInner = <TFieldValues extends FieldValues = FieldValues>({
  name,
  control: controlProp,
  parse,
  format,
  handleAfterChange,
  isMulti,
  ...props
}: RHFBlockSelectFieldProps<TFieldValues>) => {
  const formContext = useFormContext<TFieldValues>();
  const control = controlProp ?? formContext?.control;

  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({ name, control });

  const { options = [] } = props;

  const displayValue = format
    ? format(value, options)
    : isMulti
    ? (value || []).map((v: any) => options.find((o) => o.value === v) ?? { value: v, label: v })
    : value != null
    ? options.find((o) => o.value === value) ?? null
    : null;

  const handleChange = (selected: any) => {
    const parsed = parse
      ? parse(selected)
      : isMulti
      ? (selected || []).map((o: any) => o.value)
      : selected?.value ?? null;

    onChange(parsed);
    handleAfterChange?.(selected);
  };

  return (
    <BlockSelectField
      {...props}
      isMulti={isMulti}
      value={displayValue}
      onChange={handleChange}
      error={getErrorFromFieldState(error)?.[0]?.code}
    />
  );
};

export const RHFBlockSelectField = memo(
  RHFBlockSelectFieldInner
) as typeof RHFBlockSelectFieldInner;
